import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useRef, useEffect } from 'react';
import { IconFb } from '@belong/icons/dist/IconFb';
import clsx from 'clsx';
import { ButtonBase } from '../ButtonBase/ButtonBase.js';
import { Text } from '../Text/Text.js';
import css from './FacebookLogin.module.css.js';

const getParamsFromObject = params => '?' + Object.keys(params).map(param => `${param}=${encodeURIComponent(params[param])}`).join('&');
const decodeParamForKey = (paramString, key) => {
  return decodeURIComponent(paramString.replace(new RegExp('^(?:.*[&\\?]' + encodeURIComponent(key).replace(/[\.\+\*]/g, '\\$&') + '(?:\\=([^&]*))?)?.*$', 'i'), '$1'));
};
const getIsMobile = () => {
  let isMobile = false;
  try {
    isMobile = !!(window.navigator && window.navigator.standalone || navigator.userAgent.match('CriOS') || navigator.userAgent.match(/mobile/i));
  } catch (_ex) {
    // continue regardless of error
  }
  return isMobile;
};
const variantToStyles = {
  primary: 'bg-facebook-button transition-colors',
  secondary: 'border border-solid border-navy bg-white transition-all'
};
const variantToHoverStyles = {
  primary: 'hover:bg-facebook-button-active active:bg-facebook-button-active focus:bg-facebook-button-active',
  secondary: css.button 
};
const disableStyles = 'disabled:text-gray';
function FacebookLogin({
  isDisabled,
  callback,
  appId,
  xfbml = false,
  cookie = false,
  authType = '',
  scope = 'public_profile,email',
  state = 'facebookdirect',
  responseType = 'code',
  returnScopes = false,
  redirectUri = typeof window !== 'undefined' ? window.location.href : '/',
  autoLoad,
  disableMobileRedirect = false,
  isMobile = getIsMobile(),
  fields = 'name',
  version = '3.1',
  language = 'en_US',
  variant = 'primary',
  onClick,
  onFailure,
  iconOnly
}) {
  const [isSdkLoaded, setIsSdkLoaded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const _isMounted = useRef(false);
  useEffect(() => {
    _isMounted.current = true;
    if (document.getElementById('facebook-jssdk')) {
      sdkLoaded();
      return;
    }
    setFbAsyncInit();
    loadSdkAsynchronously();
    let fbRoot = document.getElementById('fb-root');
    if (!fbRoot) {
      fbRoot = document.createElement('div');
      fbRoot.id = 'fb-root';
      document.body.appendChild(fbRoot);
    }
    return () => {
      _isMounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (isSdkLoaded && autoLoad) {
      window.FB.getLoginStatus(checkLoginAfterRefresh);
    }
  }, [autoLoad]);
  function setFbAsyncInit() {
    window.fbAsyncInit = () => {
      window.FB.init({
        version: `v${version}`,
        appId,
        xfbml,
        cookie
      });
      if (_isMounted.current) {
        setIsSdkLoaded(true);
      }
      if (autoLoad || isRedirectedFromFb()) {
        window.FB.getLoginStatus(checkLoginAfterRefresh);
      }
    };
  }
  function isRedirectedFromFb() {
    const params = window.location.search;
    return decodeParamForKey(params, 'state') === 'facebookdirect' && (decodeParamForKey(params, 'code') || decodeParamForKey(params, 'granted_scopes'));
  }
  function sdkLoaded() {
    setIsSdkLoaded(true);
  }
  function loadSdkAsynchronously() {
    const element = document.getElementsByTagName('script')[0];
    const fjs = element;
    let js = element;
    if (document.getElementById('facebook-jssdk')) {
      return;
    }
    js = document.createElement('script');
    js.id = 'facebook-jssdk';
    js.src = `https://connect.facebook.net/${language}/sdk.js`;
    fjs?.parentNode?.insertBefore(js, fjs);
  }
  function responseApi(authResponse) {
    window.FB.api('/me', {
      locale: language,
      fields: fields
    }, me => {
      Object.assign(me, authResponse);
      callback(me);
    });
  }
  function checkLoginState(response) {
    if (_isMounted.current) {
      setIsProcessing(false);
    }
    if (response.authResponse) {
      responseApi(response.authResponse);
    } else {
      if (onFailure) {
        onFailure({
          status: response.status
        });
      } else {
        callback({
          status: response.status
        });
      }
    }
  }
  function checkLoginAfterRefresh(response) {
    if (response.status === 'connected') {
      checkLoginState(response);
    } else {
      window.FB.login(loginResponse => checkLoginState(loginResponse), true);
    }
  }
  function click(e) {
    if (!isSdkLoaded || isProcessing || isDisabled) {
      return;
    }
    setIsProcessing(true);
    if (typeof onClick === 'function') {
      onClick(e);
      if (e.defaultPrevented) {
        setIsProcessing(false);
        return;
      }
    }
    const params = {
      client_id: appId,
      redirect_uri: redirectUri,
      state,
      return_scopes: returnScopes,
      scope,
      response_type: responseType,
      auth_type: authType
    };
    if (isMobile && !disableMobileRedirect) {
      window.location.href = `https://www.facebook.com/dialog/oauth${getParamsFromObject(params)}`;
    } else {
      if (!window.FB) {
        if (onFailure) {
          onFailure({
            status: 'facebookNotLoaded'
          });
        }
        return;
      }
      window.FB.getLoginStatus(response => {
        if (response.status === 'connected') {
          checkLoginState(response);
        } else {
          window.FB.login(checkLoginState, {
            scope,
            return_scopes: returnScopes,
            auth_type: params.auth_type
          });
        }
      });
    }
  }
  return jsx(ButtonBase, {
    className: clsx('relative flex h-button w-full items-center justify-center rounded duration-hover', variantToStyles[variant], variantToHoverStyles[variant], disableStyles),
    onClick: click,
    disabled: !!isDisabled,
    children: jsxs("div", {
      className: clsx('flex w-full items-center', iconOnly ? 'justify-center' : 'justify-start'),
      children: [jsx("div", {
        className: clsx('flex h-lg w-lg shrink-0 justify-center', iconOnly ? 'ml-0' : 'ml-sm', variant === 'secondary' && 'rounded-full bg-facebook-button'),
        children: jsx(IconFb, {
          className: clsx('text-white', variant === 'secondary' && `${css.icon} absolute`),
          height: 30,
          width: variant === 'primary' ? 10 : 11
        })
      }), !iconOnly && jsx("div", {
        className: "mr-sm w-full",
        children: jsx(Text, {
          fontWeight: "semibold",
          className: clsx({
            'text-white': variant === 'primary'
          }),
          children: "Connect With Facebook"
        })
      })]
    })
  });
}

export { FacebookLogin };
